package com.arena_web.ui.sections.home.mobile

import androidx.compose.runtime.Composable
import com.arena_web.ui.sections.home.attachTilt
import com.arena_web.ui.utils.*
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Img

// Ad-style screenshot composition: one large phone-framed screenshot, centred,
// with a gentle tilt and glow, halftone dots + a shard accent behind. One
// screen per section — no overlapping fragments (user feedback 2026-09-10:
// stacked full-screen floats read as clutter at 390 px).

// Small semi-transparent cutout (chart / card fragment) floating over the
// phone — the ad-creative "cluster" garnish, deliberately mini so the main
// screenshot stays readable. [edgePx] offsets from the container's left or
// right edge per [onLeft]; [topPx] from the container top.
internal data class StoryCut(
    val image: String,
    val alt: String,
    val widthPx: Int,
    val topPx: Int,
    val edgePx: Int,
    val rotateDeg: Int,
    val onLeft: Boolean,
    /** Small caption rendered above the cutout (ad style, latin by design). */
    val label: String? = null,
    /** False for assets that bring their own rounded glass frame (alpha PNGs). */
    val framed: Boolean = true,
)

// Phone width at the 390-px reference; height follows the 390:844 screenshot
// aspect naturally since the phone sits in normal flow.
private const val PHONE_WIDTH_PX = 250

@Composable
internal fun StoryComposition(
    mainImage: String,
    mainAlt: String,
    mirrored: Boolean = false,
    cuts: List<StoryCut> = emptyList(),
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .styleModifier {
                property("position", "relative")
                property("display", "flex")
                property("justify-content", "center")
                property("padding-top", m(12))
                property("padding-bottom", m(16))
            }
    ) {
        // Decor behind the phone: halftone patch on one lower corner (sides
        // swap via [mirrored]). No other accents — user feedback 2026-09-10:
        // the blue shard slivers near the phone tops read as stray lines.
        HalftoneDots(
            widthPx = 120, heightPx = 100,
            modifier = Modifier.styleModifier {
                property("position", "absolute")
                property("bottom", m(30))
                property(if (mirrored) "left" else "right", m(8))
            }
        )
        // Static rotation lives on this wrapper; the inner frame owns the tilt
        // transform (attachTilt must not fight an inline transform).
        Box(
            modifier = Modifier
                .styleModifier {
                    property("transform", "rotate(${if (mirrored) 3 else -3}deg)")
                }
        ) {
            Div(
                attrs = {
                    style {
                        property("width", m(PHONE_WIDTH_PX))
                        property("padding", m(8))
                        property("border-radius", m(36))
                        property("background", "#0B1428")
                        property("border", "1px solid rgba(148, 178, 255, 0.18)")
                        property(
                            "box-shadow",
                            "0 22px 60px rgba(0, 0, 0, 0.55), 0 0 80px rgba(46, 107, 255, 0.14)"
                        )
                        property("transition", "transform 180ms ease-out")
                    }
                    ref { el ->
                        val detach = attachTilt(el)
                        onDispose { detach() }
                    }
                }
            ) {
                Img(
                    src = mainImage,
                    alt = mainAlt,
                    attrs = {
                        style {
                            property("width", "100%")
                            property("height", "auto")
                            property("display", "block")
                            property("border-radius", m(29))
                        }
                        attr("loading", "lazy")
                    },
                )
            }
        }
        // Mini cutouts ride above the phone with slight transparency, each
        // optionally captioned like the ad-creative cluster panels.
        cuts.forEach { cut ->
            Div(
                attrs = {
                    style {
                        property("position", "absolute")
                        property("top", m(cut.topPx))
                        property(if (cut.onLeft) "left" else "right", m(cut.edgePx))
                        property("width", m(cut.widthPx))
                        property("transform", "rotate(${cut.rotateDeg}deg)")
                        property("opacity", "0.95")
                        property("z-index", "2")
                    }
                }
            ) {
                cut.label?.let { label ->
                    Div(
                        attrs = {
                            style {
                                property("font-family", "Urbanist, sans-serif")
                                property("font-size", m(12))
                                property("font-weight", "500")
                                property("color", "var(--l-text-strong, #E8F0FF)")
                                property("margin-bottom", m(5))
                                property("padding-left", m(4))
                                property("text-shadow", "var(--l-text-shadow, 0 2px 8px rgba(0,0,0,0.6))")
                            }
                        }
                    ) { org.jetbrains.compose.web.dom.Text(label) }
                }
                Div(
                    attrs = {
                        style {
                            if (cut.framed) {
                                property("border-radius", m(12))
                                property("overflow", "hidden")
                                property("border", "1px solid rgba(148, 178, 255, 0.28)")
                                property("box-shadow", "0 12px 32px rgba(0, 0, 0, 0.55)")
                            } else {
                                property("filter", "drop-shadow(0 12px 28px rgba(0, 0, 0, 0.55))")
                            }
                        }
                    }
                ) {
                    Img(
                        src = cut.image,
                        alt = cut.alt,
                        attrs = {
                            style {
                                property("width", "100%")
                                property("height", "auto")
                                property("display", "block")
                            }
                            attr("loading", "lazy")
                        },
                    )
                }
            }
        }
    }
}
